import {
  Database, BarChart3, ShieldCheck, Globe, Layers, Users, Clock,
  Award, Building2, Truck, ShoppingBag, Briefcase, Package,
  TrendingUp, Target, RefreshCw, FileCheck, Cpu, Zap,
  Search, Brain, LayoutDashboard, Lock, Eye, Workflow,
  MessageSquare, ChevronDown, ChevronUp, Sparkles, LineChart,
  ArrowUpRight, Settings, AlertTriangle as TriangleAlert
} from "lucide-react";

export const DS_HERO = {
  eyebrow: "Microsoft Fabric — Data Science & Analytics",
  headline: "Simplified Intelligence for SMEs",
  headlineSuffix: "Powered by Microsoft Fabric",
  subheadline:
    "Most SMEs don't lack data. They lack structure. Asterisk simplifies first, then scales — delivering unified analytics, margin transparency, and AI-assisted executive clarity.",
  primaryCTA: { label: "Book a Data Strategy Session", href: "#ds-contact" },
  secondaryCTA: { label: "See Use Cases", href: "#ds-usecases" },
};

export const DS_STATS = [
  { value: "150%+", label: "Projected 3-Year ROI", icon: TrendingUp },
  { value: "< 1 yr", label: "Payback Period", icon: Clock },
  { value: "36", label: "Years Business Experience", icon: Award },
  { value: "24", label: "Years Microsoft ERP", icon: ShieldCheck },
];

export const DS_PROBLEM = {
  headline: "Complexity Is the Enemy of SME Analytics",
  description:
    "Your business likely runs on ERP systems, CRM platforms, inventory tools, e-commerce systems, finance applications, and spreadsheets layered on top of everything. Data lives everywhere. Insight lives nowhere.",
  painPoints: [
    "Heavy infrastructure requirements",
    "Long implementation cycles",
    "High consulting costs",
    "Internal capability strain",
    "Spreadsheet dependency",
    "No single source of truth",
  ],
  solution: "Asterisk takes a different approach. We simplify first. Then scale.",
};

export const DS_FABRIC = {
  headline: "Our Platform of Choice: Microsoft Fabric",
  description:
    "Fabric is not just reporting, not just Power BI, not just data warehousing. It is a unified data + analytics + AI platform built for modern organisations.",
  benefits: [
    { icon: Database, text: "One governed data environment" },
    { icon: Eye, text: "One version of truth" },
    { icon: Layers, text: "One scalable architecture" },
    { icon: Brain, text: "One foundation for AI" },
  ],
  conclusion: "Instead of stitching tools together, we design a consolidated intelligence layer.",
};

export const DS_USECASES = [
  {
    id: "unify",
    icon: Database,
    title: "Unify Disconnected Systems",
    description: "ERP, CRM, Inventory, Sales, Finance, E-commerce — all integrated into a governed Lakehouse architecture.",
    results: [
      "Elimination of manual consolidation",
      "Faster month-end reporting",
      "Real-time dashboards",
      "Reduced spreadsheet dependency",
    ],
  },
  {
    id: "margin",
    icon: LineChart,
    title: "SKU-Level & Margin Clarity",
    description: "For distributors, retailers, and service organisations, margin visibility is critical.",
    results: [
      "SKU-level profitability analysis",
      "Channel margin comparison",
      "Promotion ROI tracking",
      "Customer-level profit contribution",
    ],
  },
  {
    id: "inventory",
    icon: Package,
    title: "Inventory & Working Capital Optimisation",
    description: "Inventory is cash sitting on shelves. We deliver working capital intelligence, not just reporting.",
    results: [
      "Ageing inventory visibility",
      "Slow-moving SKU detection",
      "Demand forecasting by channel",
      "Safety stock modelling",
    ],
  },
  {
    id: "customer",
    icon: Users,
    title: "Customer Intelligence & Sales Acceleration",
    description: "Sales teams stop operating on instinct alone. They operate with structured intelligence.",
    results: [
      "360° customer views",
      "Customer segmentation by value",
      "Cross-sell / upsell recommendations",
      "Churn risk identification",
    ],
  },
  {
    id: "ai",
    icon: Sparkles,
    title: "Executive-Level AI with Copilot",
    description: "Leadership asks questions like 'Which SKUs saw margin decline?' and receives instant, contextual answers.",
    results: [
      "Natural-language executive queries",
      "Instant contextual answers",
      "Boardroom analytics on demand",
      "No manual report waiting",
    ],
  },
];

export const DS_METHODOLOGY = [
  {
    step: "01",
    title: "Data Landscape & Estate Assessment",
    description: "We map system architecture, data fragmentation, reporting dependencies, governance gaps, and manual processes.",
    outcome: "We identify decision friction points — not just data gaps.",
  },
  {
    step: "02",
    title: "Decision-Centric Design",
    description: "Instead of asking 'What model do you want?', we ask: Where are margins leaking? Where is working capital trapped?",
    outcome: "Data science must solve business friction.",
  },
  {
    step: "03",
    title: "Focused High-Impact Use Cases",
    description: "We prioritise 2–3 measurable use cases: margin optimisation, inventory reduction, customer profitability, cash flow forecasting.",
    outcome: "SMEs don't need 50 dashboards. They need clarity in the right places.",
  },
  {
    step: "04",
    title: "Lightweight, Governed Data Engineering",
    description: "Using Microsoft Fabric: structured Lakehouse, governed pipelines, role-based access, data lineage and compliance.",
    outcome: "We build foundations that scale — without overbuilding.",
  },
  {
    step: "05",
    title: "Progressive AI Enablement",
    description: "Only when foundations are stable: predictive analytics, forecasting, anomaly detection, automated insights.",
    outcome: "AI is layered responsibly. Not prematurely.",
  },
];

export const DS_GOVERNANCE = {
  headline: "Data Estate Management: Governance Before Glamour",
  description: "For SMEs handling financial data, supplier contracts, customer credit exposure, ESG metrics, and regulatory compliance — governance is critical.",
  capabilities: [
    { icon: Lock, text: "Enterprise-grade security" },
    { icon: Workflow, text: "Data lineage tracking" },
    { icon: Users, text: "Structured data ownership" },
    { icon: FileCheck, text: "Audit-ready reporting" },
    { icon: Globe, text: "Azure-aligned cloud compliance" },
  ],
  conclusion: "Uncontrolled data is risk. Analytics maturity grows with governance discipline.",
};

export const DS_ROI = {
  headline: "ROI-Focused, Business-Centric Deployment",
  description: "In a recent mid-market distribution engagement, projected 3-year ROI exceeded 150%, with payback achievable within the first year.",
  drivers: [
    "Inventory reduction potential",
    "Margin improvement visibility",
    "Sales uplift from customer analytics",
    "Reporting productivity gains",
  ],
  strategicShifts: [
    "Faster executive decisions",
    "Clear working capital visibility",
    "Reduced stock-out risk",
    "Structured data foundation for future AI",
  ],
  positioning: [
    { label: "A profitability engine", icon: TrendingUp },
    { label: "A working capital optimiser", icon: RefreshCw },
    { label: "A sales performance amplifier", icon: Target },
    { label: "A digital transformation foundation", icon: Cpu },
  ],
};

export const DS_OUTCOMES = [
  "Unified data estate",
  "Margin transparency",
  "Inventory optimisation",
  "Customer intelligence",
  "AI-assisted executive clarity",
  "Governance-driven growth",
];

export const DS_JOURNEY = {
  from: "Reporting what happened",
  to: "Predicting what will happen",
  beyond: "Designing what should happen",
};

export const DS_FAQ = [
  {
    q: "What is Microsoft Fabric?",
    a: "Microsoft Fabric is a unified data + analytics + AI platform. It combines data warehousing, data engineering, real-time analytics, data science, and business intelligence into one governed environment — ideal for SMEs who want to consolidate their data estate.",
  },
  {
    q: "How is Asterisk's approach different from typical analytics consultancies?",
    a: "We simplify first, then scale. We start with business friction — not technology. Our decision-centric methodology focuses on 2–3 high-impact use cases with measurable ROI, rather than building 50 dashboards nobody uses.",
  },
  {
    q: "What kind of ROI can SMEs expect?",
    a: "In a recent mid-market distribution engagement, projected 3-year ROI exceeded 150% with payback achievable within the first year — driven by inventory reduction, margin improvement, sales uplift, and reporting productivity gains.",
  },
  {
    q: "Do we need to replace our existing ERP or CRM system?",
    a: "No. Microsoft Fabric integrates with your existing systems — ERP, CRM, e-commerce, and other data sources — through governed data pipelines. We unify your data without disrupting your operations.",
  },
  {
    q: "Is Microsoft Fabric suitable for small teams?",
    a: "Absolutely. Our SME-optimised methodology is designed for lean teams with limited IT bandwidth. We handle the data engineering and governance so your team can focus on insights and decisions.",
  },
  {
    q: "How long does a data science engagement take?",
    a: "A focused engagement typically starts with a 4–6 week assessment and design phase, followed by phased deployment of prioritised use cases. First measurable insights can be delivered within 8–12 weeks.",
  },
];
